import { Component, ViewChild } from '@angular/core';
import { BackendService } from '../../core/services/backend.service';
import { SessionService } from '../../core/services/session.service';
import { ActivatedRoute } from '@angular/router';
import { CustomDatePipe } from '../../core/pipes/date.pipe';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';

@Component({
  selector: 'app-order-details',
  imports: [CustomDatePipe, MenuBarComponent, FooterComponent, AdminNavComponent, FormsModule, NgSelectModule, NgxTgModule, NgbTooltip],
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.scss'
})
export class OrderDetailsComponent {
  orderId: string = '';
  order: any;
  newComment: string = '';

  /** Options for the status dropdown (Wallio_OrderStatuses). */
  statuses: { id: string; title: string; disabled?: boolean }[] = [];
  /**
   * The status every order is created with. It used to be filtered out of the
   * dropdown as "not something anyone sets" — but an order sitting on New has
   * that status id, and ng-select renders a value missing from its items as
   * blank, so taking an order landed you on an empty selector.
   *
   * It is in the list now, but disabled: present so the dropdown can show an
   * order that is on it, unselectable so an order only ever moves on from New.
   * It also doubles as the label when an order carries no stored statusId.
   */
  private static readonly INITIAL_STATUS_TITLE = 'New';
  /** Bound to the dropdown — the id of the currently selected status. */
  statusId: string | null = null;
  savingStatus = false;
  /** True while the take request is in flight, to block a double assignment. */
  takingOrder = false;

  /**
   * Needed to roll the selection back: ng-select holds its own view state, and
   * reassigning the one-way [ngModel] does not push a reverted value into it.
   */
  @ViewChild('statusSelect') statusSelect?: NgSelectComponent;

  constructor(
    private backendService: BackendService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private i18n: TranslateService,
    private session: SessionService,
  ) {
    //get order id from url using angular router
    this.orderId = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit(): void {
    this.backendService.getOrderById(this.orderId).subscribe(order => {
      this.order = order.data.order;
      this.statusId = this.order?.statusId ?? null;
      this.selectInitialStatus();
    });
    this.backendService.getOrderStatuses().subscribe({
      next: (res) => {
        const all: { id: string; title: string }[] = res?.data?.statuses ?? [];
        this.statuses = all.map((s) => ({
          ...s,
          disabled: s.title === OrderDetailsComponent.INITIAL_STATUS_TITLE,
        }));
        this.selectInitialStatus();
      },
      error: () => { this.statuses = []; },
    });
  }

  /**
   * An order with no stored statusId is still New, so point the dropdown at it
   * rather than leaving it blank.
   *
   * Called from both subscriptions because they race: whichever lands second is
   * the first point where both the order and the options are known. It only
   * ever fills a gap — a status the order already has is left alone.
   */
  private selectInitialStatus(): void {
    if (this.statusId || !this.order || !this.statuses.length) return;
    this.statusId = this.statuses.find(
      (s) => s.title === OrderDetailsComponent.INITIAL_STATUS_TITLE,
    )?.id ?? null;
  }

  /**
   * Label shown in place of the dropdown while the order is unassigned — whoever
   * takes it owns moving it on from 'New'.
   */
  get currentStatusTitle(): string {
    return this.order?.status
      ?? this.statuses.find((s) => s.id === this.statusId)?.title
      ?? OrderDetailsComponent.INITIAL_STATUS_TITLE;
  }

  /**
   * Persists a new status. The dropdown moves immediately and is rolled back if
   * the request fails, so it never shows a status the backend did not accept.
   */
  public onStatusChange(statusId: string | null): void {
    if (!statusId || statusId === this.order?.statusId) return;
    // Belt and braces: the dropdown is not rendered while unassigned.
    if (!this.order?.assignedTo) return;
    // ng-select will not emit a disabled option, so this only guards a
    // programmatic call — an order never moves back to New.
    if (this.statuses.find((s) => s.id === statusId)?.disabled) return;

    const previousId = this.order?.statusId ?? null;
    this.statusId = statusId;
    this.savingStatus = true;

    this.backendService.updateOrderStatus(this.orderId, statusId)
      .pipe(finalize(() => { this.savingStatus = false; }))
      .subscribe({
        next: () => {
          this.order.statusId = statusId;
          this.order.status = this.statuses.find(s => s.id === statusId)?.title ?? this.order.status;
          this.toastr.success(this.i18n.instant('order_details_toast_status_updated'));
        },
        error: () => {
          this.statusId = previousId;
          this.statusSelect?.writeValue(previousId);
          this.toastr.error(this.i18n.instant('order_details_toast_status_error'));
        },
      });
  }

  public onEnterPress(event: any): void {
    // If Shift+Enter → allow newline
    if (event.shiftKey) return;

    // Prevent default "newline" behavior
    event.preventDefault();

    // Only call addComment if there's text
    if (this.newComment.trim()) {
      this.addComment();
    }
  }

  public addComment(): void {
    // Example logic
    const comment = {
      comment: this.newComment.trim(),
      date: new Date(),
      // The backend attributes the note to the signed-in admin; show the same
      // person here rather than a placeholder until the next reload.
      commentedBy: this.session.username() || this.i18n.instant('order_details_you')
    };

    // Push to UI list (if needed)
    this.order.comments.push(comment);

    this.backendService.addComment(this.orderId, comment.comment).subscribe();

    // Clear input
    this.newComment = '';
  }

  /** Assigns the order to the signed-in admin. */
  public takeOrder(order: any): void {
    if (this.takingOrder) return;
    this.takingOrder = true;

    this.backendService.takeOrder(order.id)
      .pipe(finalize(() => { this.takingOrder = false; }))
      .subscribe({
        next: (response) => {
          this.order.assignedTo = response?.data?.assignedTo ?? null;
          this.toastr.success(this.i18n.instant('order_details_toast_assigned'));
        },
        // it failed silently before: the Take button just stayed put
        error: () => this.toastr.error(this.i18n.instant('order_details_toast_take_error')),
      });
  }

}
